import { useEffect, useState, useImperativeHandle } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
  Switch,
  InputAdornment,
} from "@mui/material";
import { FiArrowLeft, FiCalendar, FiClock } from "react-icons/fi";
import { MdPets, MdCurrencyRupee } from "react-icons/md";
import PetsIcon from "@mui/icons-material/Pets";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import NotesIcon from "@mui/icons-material/Notes";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import RepeatIcon from "@mui/icons-material/Repeat";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import React from "react";
import { parse, isBefore } from "date-fns";
// import AddPetForm from './AddPetForm';
import PetCard from "./PetCard";
import AddNewPet from "./AddNewPet";

// Helper to convert 24-hour "HH:mm" or "HH:mm:ss" time string ➜ locale 12-hour e.g. "8:15 AM"
const convertTo12Hour = (time24) => {
  if (!time24) return "";
  // If already contains AM/PM, assume correct
  if (/\b(AM|PM)$/i.test(time24.trim())) {
    return time24;
  }
  const parts = time24.split(":");
  if (parts.length < 2) return time24;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1].padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

// Helper to convert 12-hour time string to 24-hour format
const convertTo24Hour = (time12h) => {
  if (!time12h) return "";
  const match = time12h.match(/^(\d{1,2}):(\d{2}) ?([APMapm]{2})$/);
  if (!match) return time12h; // fallback to original if not matching
  let [_, hours, minutes, modifier] = match;
  hours = parseInt(hours, 10);
  if (modifier.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

// Add this component after the imports and before the main component
const ReadOnlyDisplay = ({
  label,
  value,
  placeholder,
  bgColor = "#f9fafb",
  borderColor = "#e5e7eb",
  textColor = "#6b7280",
}) => (
  <Box>
    <Typography
      variant="subtitle2"
      fontWeight="bold"
      mb={0.5}
      className="flex items-center gap-2"
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1.5,
        borderRadius: "12px",
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        minHeight: "40px",
        position: "relative",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: value ? "#374151" : textColor,
          fontStyle: value ? "normal" : "italic",
          flex: 1,
        }}
      >
        {value || placeholder}
      </Typography>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: "8px",
          transform: "translateY(-50%)",
          fontSize: "12px",
          color: "#9ca3af",
          fontWeight: 500,
          backgroundColor: bgColor,
          px: 1,
          borderRadius: "4px",
        }}
      >
        Auto
      </Box>
    </Box>
  </Box>
);

const DogWalkingDrawer = React.forwardRef(
  (
    {
      isOpen,
      onClose,
      onUpdate,
      clientId,
      itemData,
      orderId,
      fromCalendar = false,
      selectedSlotInfo,
      renderAsContent = false,
      editMode = false,
      onRefreshData = null,
      appointmentStatusRef = null,
      serviceTypeId = null,
      onPetFormStateChange = null,
      autoOpenPetForm = false,
      isPetDetails = false,
    },
    ref,
  ) => {
    const [services, setServices] = useState([]);
    const [staffs, setStaffs] = useState([]);
    const [serviceDetailData, setServiceDetailData] = useState([]);
    const [servicesd, setServicesd] = useState({});
    const [availableFrequencies, setAvailableFrequencies] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    // Minimum selectable date is today
    const todayStr = new Date().toISOString().split("T")[0];
    const [isPetDisabled, setIsPetDisabled] = useState(false);

    // Helper to determine if a time slot is in the past relative to now
    const isPastTimeSlot = (slotLabel, selectedDate) => {
      if (!selectedDate) return false; // No date selected yet
      try {
        const slotDate = parse(
          `${selectedDate} ${slotLabel}`,
          "yyyy-MM-dd h:mm a",
          new Date(),
        );
        return isBefore(slotDate, new Date());
      } catch {
        return false;
      }
    };
    const [showNewPetForm, setShowNewPetForm] = useState(false);
    const [isEditPet, setIsEditPet] = useState(false);
    const [pets, setPets] = useState([]);
    const [newPet, setNewPet] = useState({
      type: "",
      name: "",
      gender: "",
      size: "",
      breed: "",
      age: "",
      birth_date: "",
      notes: "",
      tags: [],
      weight_kg: "",
      behavior_notes: "",
    });

    // Add state for validation errors
    const [petFormErrors, setPetFormErrors] = useState({});

    const [petService, setPetService] = useState({
      pet_id: "",
      appointment_date: "",
      appointment_start_time: "",
      service_id: "",
      duration: "",
      price: 0,
      discount: 0,
      gst: 0,
      gst_amount: 0,
      totalAmount: 0,
      appointment_to: "",
      quantity: 1,
      frequency: "", // This will store plan type (one_time, weekly, monthly, quaterly)
      numberOfTimes: "", // This will store service frequency (once, twice)
      notes: "",
    });

    // State for tracking if this is from queue drop with original time
    const [isFromQueueWithOriginalTime, setIsFromQueueWithOriginalTime] =
      useState(false);
    const [originalQueueTime, setOriginalQueueTime] = useState(null);

    // const [petTypes, setPetTypes] = useState([]);
    // const [petSizes, setPetSizes] = useState([]);
    // const [petBreeds, setPetBreeds] = useState([]);
    // const [selectedServiceId, setSelectedServiceId] = useState('');
    const [searchTerm, setSearchTerm] = useState("");
    const [petDetails, setPetDetails] = useState([]);
    const [editDataFetched, setEditDataFetched] = useState(false);
    const [showTimeChangeDialog, setShowTimeChangeDialog] = useState(false);
    const [pendingTimeChange, setPendingTimeChange] = useState(null);
    const [originalTimeData, setOriginalTimeData] = useState(null);

    // Add payment state
    const [paymentMethod, setPaymentMethod] = useState("");
    const [showPaymentFields, setShowPaymentFields] = useState(false);
    const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({
      upiId: "",
      cardNumber: "",
      cardHolderName: "",
      cardExpiry: "",
      cardCvv: "",
      cashAmount: "",
    });

    const handlePaymentDetailsChange = (field, value) => {
      setPaymentDetails((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handlePayNowClick = () => {
      setShowPaymentDropdown(!showPaymentDropdown);
    };

    // Get frequency options (plan types) from available services
    const getFrequencyOptions = () => {
      const allPlanTypes = new Set();
      services.forEach((service) => {
        if (service.pricing) {
          Object.values(service.pricing).forEach((frequencyPricing) => {
            Object.keys(frequencyPricing).forEach((planType) => {
              allPlanTypes.add(planType);
            });
          });
        }
      });

      return Array.from(allPlanTypes).map((planType) => {
        let label = "";
        switch (planType) {
          case "one_time":
            label = "One Time";
            break;
          case "weekly":
            label = "Weekly";
            break;
          case "monthly":
            label = "Monthly";
            break;
          case "quaterly":
            label = "Quarterly";
            break;
          default:
            label = planType.charAt(0).toUpperCase() + planType.slice(1);
        }
        return { value: planType, label };
      });
    };

    useEffect(() => {
      if (isPetDetails && (itemData?.pet_id || itemData?.vendor_pet_id)) {
        setIsPetDisabled(true);
        if (!petService.pet_id && (itemData.pet_id || itemData.vendor_pet_id)) {
          setPetService((prev) => ({
            ...prev,
            pet_id: itemData.pet_id || itemData.vendor_pet_id,
          }));
          // Fetch pet details immediately
          fetchPetDetails(itemData.pet_id || itemData.vendor_pet_id);
        }
      }
    }, [isPetDetails, itemData, petService.pet_id]);

    // Get number of times options (service frequencies) based on selected frequency
    const getNumberOfTimesOptions = () => {
      if (!petService.frequency) return [];

      const availableServiceFrequencies = new Set();
      services.forEach((service) => {
        if (service.pricing) {
          Object.keys(service.pricing).forEach((serviceFreq) => {
            if (
              service.pricing[serviceFreq][petService.frequency] !== undefined
            ) {
              availableServiceFrequencies.add(serviceFreq);
            }
          });
        }
      });

      return Array.from(availableServiceFrequencies).map((serviceFreq) => {
        let label = "";
        switch (serviceFreq) {
          case "once":
            label = "Once";
            break;
          case "twice":
            label = "Twice";
            break;
          default:
            label = serviceFreq.charAt(0).toUpperCase() + serviceFreq.slice(1);
        }
        return { value: serviceFreq, label };
      });
    };

    // Get services based on selected frequency and numberOfTimes
    const getAvailableServices = () => {
      if (!petService.frequency || !petService.numberOfTimes) {
        return [];
      }

      const availableServices = services.filter((service) => {
        if (!service.pricing) return false;
        return (
          service.pricing[petService.numberOfTimes] &&
          service.pricing[petService.numberOfTimes][petService.frequency] !==
            undefined
        );
      });
      return availableServices;
    };

    // Check if all required fields are filled to show services
    const canShowServices = () => {
      const canShow = petService.frequency && petService.numberOfTimes;

      return canShow;
    };

    const renderPaymentFields = () => {
      if (!showPaymentFields) return null;

      switch (paymentMethod) {
        case "upi":
          return (
            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                borderRadius: 3,
                border: "2px solid #bbf7d0",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <QrCodeIcon sx={{ fontSize: 24, color: "#10b981" }} />
                <Typography variant="h6" fontWeight="bold" color="#059669">
                  UPI Payment
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={paymentDetails.upiId}
                onChange={(e) =>
                  handlePaymentDetailsChange("upiId", e.target.value)
                }
                placeholder="Enter UPI ID (e.g., user@upi)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeIcon sx={{ color: "#10b981", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    backgroundColor: "white",
                    border: "2px solid transparent",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "#bbf7d0",
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                    },
                    "&.Mui-focused": {
                      borderColor: "#10b981",
                      boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                    },
                  },
                }}
              />
            </Box>
          );

        case "card":
          return (
            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                borderRadius: 3,
                border: "2px solid #bfdbfe",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <CreditCardIcon sx={{ fontSize: 24, color: "#6366f1" }} />
                <Typography variant="h6" fontWeight="bold" color="#4f46e5">
                  Card Payment
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    value={paymentDetails.cardNumber}
                    onChange={(e) =>
                      handlePaymentDetailsChange("cardNumber", e.target.value)
                    }
                    placeholder="Enter card number"
                    inputProps={{ maxLength: 16 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CreditCardIcon
                            sx={{ color: "#6366f1", fontSize: 18 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: "white",
                        border: "2px solid transparent",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#bfdbfe",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6366f1",
                          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    value={paymentDetails.cardHolderName}
                    onChange={(e) =>
                      handlePaymentDetailsChange(
                        "cardHolderName",
                        e.target.value,
                      )
                    }
                    placeholder="Enter card holder name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "#6366f1", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: "white",
                        border: "2px solid transparent",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#bfdbfe",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6366f1",
                          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    value={paymentDetails.cardExpiry}
                    onChange={(e) =>
                      handlePaymentDetailsChange("cardExpiry", e.target.value)
                    }
                    placeholder="MM/YY"
                    inputProps={{ maxLength: 5 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EventIcon sx={{ color: "#6366f1", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: "white",
                        border: "2px solid transparent",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#bfdbfe",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6366f1",
                          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    value={paymentDetails.cardCvv}
                    onChange={(e) =>
                      handlePaymentDetailsChange("cardCvv", e.target.value)
                    }
                    placeholder="CVV"
                    inputProps={{ maxLength: 4 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <span
                            style={{
                              color: "#6366f1",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            •••
                          </span>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        backgroundColor: "white",
                        border: "2px solid transparent",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#bfdbfe",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6366f1",
                          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          );

        case "cash":
          return (
            <Box
              sx={{
                mt: 3,
                p: 3,
                bgcolor: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                borderRadius: 3,
                border: "2px solid #fde68a",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <AccountBalanceWalletIcon
                  sx={{ fontSize: 24, color: "#f59e0b" }}
                />
                <Typography variant="h6" fontWeight="bold" color="#d97706">
                  Cash Payment
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 2,
                  border: "2px solid #fde68a",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "50%",
                    bgcolor:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountBalanceWalletIcon
                    sx={{ color: "white", fontSize: 28 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="#92400e"
                    mb={0.5}
                  >
                    Cash Payment Selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Payment will be collected in cash at the time of service
                    delivery
                  </Typography>
                </Box>
              </Box>
            </Box>
          );

        default:
          return null;
      }
    };

    const [formData, setFormData] = useState({
      order_type: "offline",
      vendor_client_id: "",
      order_items: [
        {
          item_type: "dog_walking",
          vendor_pet_id: "",
          service_id: "",
          technician_id: "",
          start_date: "",
          start_time: "",
          duration: "",
          quantity: 1,
          unit_price: 0,
          discount: 0,
          gst: 0,
          gst_amount: 0,
          total_price: 0,
          addons: [],
        },
      ],
    });

    // Add effect to automatically open pet form when triggered from Save & Add Pet flow
    useEffect(() => {
      // Check if we should auto-open the pet form (triggered by Save & Add Pet flow)
      if (
        isOpen &&
        fromCalendar &&
        !editMode &&
        pets?.length === 0 &&
        !showNewPetForm &&
        !editDataFetched &&
        autoOpenPetForm
      ) {
        // If there are no pets for this client and we're coming from calendar with explicit autoOpenPetForm flag, auto-open pet form
        setTimeout(() => {
          setShowNewPetForm(true);
        }, 500);
      }
    }, [
      isOpen,
      fromCalendar,
      editMode,
      pets,
      showNewPetForm,
      editDataFetched,
      autoOpenPetForm,
    ]);

    // Fetch initial data
    useEffect(() => {
      if (isOpen) {
        const fetchInitialData = async () => {
          try {
            // Use clientId from itemData if available (edit mode), otherwise use passed clientId
            const effectiveClientId = itemData?.vendor_client_id || clientId;

            if (!effectiveClientId) {
              return;
            }

            const [
              petsRes,
              // typesRes,
              // breedsRes,
              servicesRes,
              staffResponse,
            ] = await Promise.all([
              httpService.get(
                `/vendorpet/getvendorclientpets/${effectiveClientId}`,
              ),
              // httpService.get('/petTypes/getPetTypes'),
              // httpService.get('/petBreeds/getPetBreeds'),
              httpService.get(
                "/vendordogwalkingservice/getvendordogwalkingservices?status=active",
              ),
              httpService.get("/employees/getvendoremployees"),
            ]);
            const dogs = petsRes.data.filter(
              (pet) => pet.pet_type?.name === "Dog",
            );
            setPets(dogs);
            // setPetTypes(typesRes.data);
            // setPetBreeds(breedsRes.data);

            // Process dog walking services and extract plan type options
            const dogWalkingServices = servicesRes.data || [];
            setServices(dogWalkingServices);

            // Extract unique plan types from all services pricing
            const planTypes = new Set();
            dogWalkingServices.forEach((service) => {
              if (service.pricing) {
                Object.values(service.pricing).forEach((frequencyPricing) => {
                  Object.keys(frequencyPricing).forEach((planType) => {
                    planTypes.add(planType);
                  });
                });
              }
            });
            setAvailableFrequencies(Array.from(planTypes));
            setStaffs(staffResponse.data);
          } catch (error) {
            console.error("Error loading initial data", error);
          }
        };
        fetchInitialData();
      }
    }, [isOpen, clientId, itemData?.vendor_client_id, serviceTypeId]);

    // Auto-populate from calendar selection
    useEffect(() => {
      if (fromCalendar && selectedSlotInfo && !editDataFetched) {
        setPetService((prev) => ({
          ...prev,
          appointment_date: selectedSlotInfo.date,
          appointment_start_time: selectedSlotInfo.startTime,
        }));
        setEditDataFetched(true);
      }
    }, [fromCalendar, selectedSlotInfo, editDataFetched]);

    // Auto-select pet when pets list loads and we have vendor_pet_id from itemData
    useEffect(() => {
      if (
        pets?.length > 0 &&
        itemData &&
        petService.pet_id !== itemData.vendor_pet_id
      ) {
        let foundPet = null;

        // Try to match by vendor_pet_id first
        if (itemData.vendor_pet_id) {
          foundPet = pets.find((pet) => pet._id === itemData.vendor_pet_id);
        }

        // If not found by ID, try to match by pet_name
        if (!foundPet && itemData.pet_name) {
          foundPet = pets.find(
            (pet) =>
              pet.name &&
              pet.name.toLowerCase() === itemData.pet_name.toLowerCase(),
          );
        }

        if (foundPet) {
          setPetService((prev) => ({ ...prev, pet_id: foundPet._id }));
          fetchPetDetails(foundPet._id);
        }
      }
    }, [pets, itemData?.vendor_pet_id, itemData?.pet_name, petService.pet_id]);

    // Auto-select employee when staffs list loads (only if not already chosen)
    useEffect(() => {
      if (staffs?.length > 0 && itemData && !petService.appointment_to) {
        let foundStaff = null;

        // Try to match by employee_id first
        if (itemData.employee_id) {
          foundStaff = staffs.find(
            (staff) => staff._id === itemData.employee_id,
          );
        }

        // If not found by ID, try to match by employee_name
        if (!foundStaff && itemData.employee_name) {
          foundStaff = staffs.find(
            (staff) =>
              staff.name &&
              staff.name.toLowerCase() === itemData.employee_name.toLowerCase(),
          );
        }

        if (foundStaff) {
          setPetService((prev) => ({
            ...prev,
            appointment_to: foundStaff._id,
          }));
        }
      }
    }, [staffs, itemData]);

    // Auto-select service when services list loads and we have service_id from itemData
    useEffect(() => {
      if (
        services?.length > 0 &&
        itemData?.service_id &&
        petService.service_id !== itemData.service_id
      ) {
        const foundService = services.find(
          (service) => service._id === itemData.service_id,
        );
        if (foundService) {
          // In edit mode, just set the service_id without changing price/duration from API
          setPetService((prev) => ({
            ...prev,
            service_id: itemData.service_id,
          }));
          // Fetch service details to get additional info
          fetchServiceDetails(itemData.service_id);
        }
      }
    }, [services, itemData?.service_id, petService.service_id, petDetails]);

    // Auto-fetch service details when service_id is set from edit mode
    useEffect(() => {
      if (editMode && petService.service_id && !serviceDetailData._id) {
        fetchServiceDetails(petService.service_id);
      }
    }, [editMode, petService.service_id, serviceDetailData._id]);

    // Auto-fetch service details when service_id changes
    useEffect(() => {
      if (petService.service_id && services?.length > 0) {
        fetchServiceDetails(petService.service_id);
      }
    }, [petService.service_id, services, petDetails]);

    // Ensure service details are fetched when drawer opens in edit mode
    useEffect(() => {
      if (
        isOpen &&
        editMode &&
        petService.service_id &&
        !serviceDetailData._id
      ) {
        fetchServiceDetails(petService.service_id);
      }
    }, [isOpen, editMode, petService.service_id, serviceDetailData._id]);

    // Call price API when all required fields are available
    useEffect(() => {
      if (
        petService.service_id &&
        petService.frequency &&
        petService.numberOfTimes
      ) {
        calculatePriceFromAPI("dog_walking");
      }
    }, [petService.service_id, petService.frequency, petService.numberOfTimes]);

    // Ensure price API is called when all data is loaded in edit mode
    useEffect(() => {
      if (
        isOpen &&
        editMode &&
        petService.service_id &&
        petService.frequency &&
        petService.numberOfTimes &&
        serviceDetailData._id
      ) {
        calculatePriceFromAPI("dog_walking");
      }
    }, [
      isOpen,
      editMode,
      petService.service_id,
      petService.frequency,
      petService.numberOfTimes,
      serviceDetailData._id,
    ]);

    // useEffect(() => {
    //   const fetchSizes = async () => {
    //     if (!newPet.type) {
    //       setPetSizes([]);
    //       return;
    //     }
    //     const selectedType = petTypes.find((t) => t._id === newPet.type);
    //     if (selectedType?._id) {
    //       try {
    //         const res = await httpService.get(`/vendorpetsize/getVendorPetSizes?petTypeId=${selectedType._id}`);
    //         setPetSizes(res.data);
    //       } catch (error) {
    //         console.error('Error fetching pet sizes', error);
    //       }
    //     }
    //   };
    //   fetchSizes();
    // }, [newPet.type, petTypes]);

    // Auto-calculate total when price, discount, or addons change
    useEffect(() => {
      const { total } = calculateTotal();

      if (petService.totalAmount !== total) {
        setPetService((prev) => ({ ...prev, totalAmount: total }));
      }
    }, [
      petService.price,
      petService.discount,
      petService.gst,
      servicesd,
      serviceDetailData,
    ]);

    const handlePetServiceChange = (field, value) => {
      setPetService((prev) => ({ ...prev, [field]: value }));
    };

    const handleAddonToggle = (key) => {
      setServicesd((prev) => {
        const updated = { ...prev };
        updated[key] = { ...updated[key], selected: !updated[key].selected };
        return updated;
      });

      // Extract selected addons from updatedServices
      const updatedServices = { ...servicesd };
      updatedServices[key] = {
        ...updatedServices[key],
        selected: !updatedServices[key].selected,
      };

      // const selectedAddons = Object.entries(updatedServices)
      //   .filter(([_, addon]) => addon.selected)
      //   .map(([addonId, addon]) => ({
      //     addon_id: addonId,
      //     addon_price: Number(addon.price),
      //     name: addon.label,
      //   }));

      // Update formData with selected addons
      // setPetService((prev) => ({
      //   ...prev,
      //   addons: selectedAddons,
      // }));
    };

    const handleServicePriceChange = (key, newPrice) => {
      setServicesd((prev) => ({
        ...prev,
        [key]: { ...prev[key], price: newPrice },
      }));
    };

    // Effect to notify parent about pet form state changes
    useEffect(() => {
      if (onPetFormStateChange && renderAsContent) {
        onPetFormStateChange(showNewPetForm);
      }
    }, [showNewPetForm, onPetFormStateChange, renderAsContent]);

    // 🚫 Lock body scroll while drawer is open
    useEffect(() => {
      if (isOpen) {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
      } else {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      }
      return () => {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      };
    }, [isOpen]);

    const generateTimeSlots = () => {
      const slots = [];
      let start = 8 * 60; // 8:00 AM in minutes
      const end = 20 * 60; // 8:00 PM in minutes

      while (start <= end) {
        const hours = Math.floor(start / 60);
        const minutes = start % 60;
        const formatted = new Date(0, 0, 0, hours, minutes).toLocaleTimeString(
          "en-IN",
          {
            hour: "numeric",
            minute: "2-digit",
          },
        );
        slots.push(formatted);
        start += 15;
      }
      return slots;
    };

    const timeSlots = generateTimeSlots();

    // Helper function to format duration from new API structure
    const formatDuration = (durationData, petSize = null) => {
      if (!durationData) return "";

      let targetDuration = null;

      if (durationData.duration_based_on === "flat") {
        targetDuration = durationData.flat;
      } else {
        // Size-based duration
        const sizeKey = petSize?.toLowerCase() || "flat";
        const validSizes = ["small", "medium", "large", "giant"];
        const selectedSize = validSizes.includes(sizeKey) ? sizeKey : "flat";
        targetDuration = durationData[selectedSize] || durationData.flat;
      }

      if (!targetDuration) return "";

      const { hour = 0, minutes = 0 } = targetDuration;

      if (hour === 0 && minutes === 0) return "";

      const parts = [];
      if (hour > 0) {
        parts.push(`${hour} Hour${hour > 1 ? "s" : ""}`);
      }
      if (minutes > 0) {
        parts.push(`${minutes} Minute${minutes > 1 ? "s" : ""}`);
      }

      return parts.join(" ");
    };

    const calculatePriceFromAPI = async (serviceType) => {
      // Check if all required fields are available for API call
      if (
        !petService.service_id ||
        !petService.frequency ||
        !petService.numberOfTimes
      ) {
        return;
      }

      try {
        const requestPayload = {
          vendor_service_id: petService.service_id,
          frequency: petService.frequency,
          number_of_times: petService.numberOfTimes,
        };

        const response = await httpService.post(
          `/vendorbooking/getPriceCalculation?service_type=${serviceType}`,
          requestPayload,
        );

        // Check if response exists and handle different response structures
        if (response && response.data !== undefined) {
          let calculatedPrice = 0;
          let gstAmount = 0;
          if (typeof response.data === "number") {
            // Direct number response
            calculatedPrice = response.data;
          } else if (
            typeof response.data === "object" &&
            response.data !== null
          ) {
            // Object response - check for fields
            calculatedPrice = response.data.price || 0;
            gstAmount = response.data.taxAmount || 0;
          } else {
            // Fallback
            calculatedPrice = 0;
          }
          setPetService((prev) => ({
            ...prev,
            price: calculatedPrice,
            basic_price: calculatedPrice,
            gst_amount: gstAmount, // store GST separately if needed
          }));
        } else {
          console.error(
            "🔍 DogWalkingDrawer - Invalid response structure:",
            response,
          );
          toast.error(
            "Failed to calculate price: Invalid response from server",
          );
        }
      } catch (error) {
        console.error(
          "🔍 DogWalkingDrawer - Error calling price calculation API:",
          error,
        );
        if (error.response) {
          console.error(
            "🔍 DogWalkingDrawer - Error response:",
            error.response.data,
          );
          toast.error(
            `Failed to calculate price: ${error.response.data?.message || "Server error"}`,
          );
        } else {
          toast.error(
            "Failed to calculate price. Please check your connection and try again.",
          );
        }
      }
    };

    const fetchServiceDetails = async (serviceId) => {
      try {
        const res = await httpService.get(
          `/vendordogwalkingservice/getvendordogwalkingservice/${serviceId}`,
        );
        const serviceDetails = res.data;

        // Handle addons if available
        if (
          serviceDetails?.adon_services &&
          Array.isArray(serviceDetails.adon_services)
        ) {
          // Safely create a Set of selected addon IDs, or an empty Set if not present
          const selectedIds = new Set(
            itemData?.addons?.map((item) => item.addon_id) || [],
          );

          const mapped = {};
          serviceDetails.adon_services.forEach((addon) => {
            mapped[addon.adon_id] = {
              selected: selectedIds.has(addon.adon_id), // will be false if Set is empty
              price: addon.value === "Free" ? 0 : parseFloat(addon.value),
              label: addon.name || "",
            };
          });
          setServicesd(mapped);

          // Extract selected addons from updatedServices
          // const selectedAddons = Object.entries(mapped)
          //   .filter(([_, addon]) => addon.selected)
          //   .map(([addonId, addon]) => ({
          //     addon_id: addonId,
          //     addon_price: Number(addon.price),
          //     name: addon.label,
          //   }));

          // Update formData with selected addons
          setPetService((prev) => ({
            ...prev,
            // addons: selectedAddons,
            tax_percentage: serviceDetails.tax_percentage || 0,
          }));
        } else {
          // No addons available, but still set tax_percentage
          setPetService((prev) => ({
            ...prev,
            tax_percentage: serviceDetails.tax_percentage || 0,
          }));
          setServicesd({}); // Clear addons
        }

        // Update petService with service details using new duration formatting
        // IMPORTANT: Don't override frequency and numberOfTimes if they're already set
        setPetService((prev) => ({
          ...prev,
          duration: formatDuration(
            serviceDetails?.duration,
            petDetails?.size?.size_label,
          ),
          service_name: serviceDetails?.name || "",
          service_category_name:
            serviceDetails?.service_category?.service_category_name || "",
          service_type_name:
            serviceDetails?.service_category?.service_type_name || "",
          // Keep existing frequency and numberOfTimes, don't override them
        }));

        // Also set serviceDetailData for other parts of the component that use it
        setServiceDetailData(serviceDetails);

        return serviceDetails;
      } catch (error) {
        console.error("Error fetching service details", error);
        return null;
      }
    };

    const fetchPetDetails = async (petId) => {
      try {
        const res = await httpService.get(`/vendorpet/getvendorpet/${petId}`);
        setPetDetails(res.data);
      } catch (error) {
        console.error("Error fetching pet details", error);
      }
    };

    const handleAddNewPet = async (newPet) => {
      try {
        const petData = {
          name: newPet.name,
          pet_type: newPet.type,
          gender: newPet.gender,
          size: newPet.size,
          breed: newPet.breed,
          age: newPet.age,
          birth_date: newPet.birth_date,
          notes: newPet.notes,
          tags: newPet.tags,
          weight_kg: newPet.weight_kg,
          behavior_notes: newPet.behavior_notes,
          vendor_client_id: clientId,
        };
        const response = await httpService.post(
          "/vendorpet/addvendorpet",
          petData,
        );
        if (response) {
          toast.success("Pet added successfully");
          setShowNewPetForm(false);
          // Reset new pet form
          setNewPet({
            type: "",
            name: "",
            gender: "",
            size: "",
            breed: "",
            age: "",
            birth_date: "",
            notes: "",
            tags: [],
            weight_kg: "",
            behavior_notes: "",
          });
          const newPetData = response.data;
          setPetService((prev) => ({
            ...prev,
            pet_id: newPetData._id,
            petDetails: newPetData,
          }));
          fetchPetDetails(newPetData._id);
          setPets((prevPets) => [...prevPets, newPetData]);
          setPetFormErrors({});
        }
      } catch (error) {
        console.error("Error adding pet:", error);
        toast.error("Failed to add pet");
      }
    };

    // const handleNewPetSave = () => {
    //   // Validation for required fields
    //   const errors = {};
    //   if (!newPet.name || newPet.name.trim() === '') errors.name = 'Pet Name is required';
    //   if (!newPet.type || newPet.type.trim() === '') errors.type = 'Type is required';
    //   if (!newPet.size || newPet.size.trim() === '') errors.size = 'Size is required';
    //   if (!newPet.breed || newPet.breed.trim() === '') errors.breed = 'Breed is required';
    //   setPetFormErrors(errors);
    //   if (Object.keys(errors).length > 0) {
    //     toast.warn('Please fill all required fields');
    //     return;
    //   }
    //   handleAddNewPet(newPet);
    //   setShowNewPetForm(false);
    //   setNewPet({
    //     type: '',
    //     name: '',
    //     gender: '',
    //     size: '',
    //     breed: '',
    //     age: '',
    //     birth_date: '',
    //     weight_kg: '',
    //     behavior_notes: '',
    //     tags: [],
    //   });
    //   setPetFormErrors({});
    // };

    const resetFormData = () => {
      setPetService({
        pet_id: "",
        appointment_date: "",
        appointment_start_time: "",
        service_id: "",
        duration: "",
        price: 0,
        discount: 0,
        gst: 0,
        gst_amount: 0,
        totalAmount: 0,
        appointment_to: "",
        quantity: 1,
        frequency: "",
        numberOfTimes: "",
        notes: "",
      });
      setFormData({
        order_type: "offline",
        vendor_client_id: "",
        order_items: [
          {
            item_type: "dog_walking",
            vendor_pet_id: "",
            service_id: "",
            technician_id: "",
            start_date: "",
            start_time: "",
            duration: "",
            quantity: 1,
            unit_price: 0,
            discount: 0,
            gst: 0,
            gst_amount: 0,
            total_price: 0,
            addons: [],
            frequency: "",
            numberOfTimes: "",
          },
        ],
      });
      setEditDataFetched(false);
      setPetFormErrors({});
      // Reset payment state
      setPaymentMethod("");
      setShowPaymentFields(false);
      setShowPaymentDropdown(false);
      setPaymentDetails({
        upiId: "",
        cardNumber: "",
        cardHolderName: "",
        cardExpiry: "",
        cardCvv: "",
        cashAmount: "",
      });
    };

    // Reset edit flag when drawer closes or itemData changes
    useEffect(() => {
      if (!isOpen) {
        setEditDataFetched(false);
      }
    }, [isOpen]);

    useEffect(() => {
      setEditDataFetched(false);
    }, [itemData]);

    // Calculate total price including discount and addons
    const calculateTotal = () => {
      const basePrice = parseFloat(petService.price) || 0;
      const discount = parseFloat(petService.discount) || 0;
      const gst = parseFloat(petService.gst_amount) || 0;

      // Calculate addons total
      const addonsTotal = Object.values(servicesd || {}).reduce(
        (sum, addon) => {
          const addonPrice = parseFloat(addon.price);
          if (addon.selected && !isNaN(addonPrice)) {
            return sum + addonPrice;
          }
          return sum;
        },
        0,
      );

      const subtotalRaw = basePrice + addonsTotal - discount;
      const subtotal = Math.max(0, subtotalRaw);
      const total = subtotal + gst;

      return {
        basePrice,
        addonsTotal,
        discount,
        gst,
        subtotal,
        total: isNaN(total) ? 0 : total,
      };
    };

    // Get selected addons from servicesd state
    // const selectedAddons = Object.entries(servicesd || {})
    //   .filter(([_, addon]) => addon.selected)
    //   .map(([addonId, addon]) => ({
    //     addon_id: addonId,
    //     addon_price: Number(addon.price),
    //     name: addon.label,
    //   }));

    const handleSaveOrders = async () => {
      if (!petService.pet_id || !petService.service_id) {
        toast.warn("Please fill all required fields");
        return;
      }

      // Validate service selection
      if (!petService?.service_id) {
        toast.warn("Please select a service", {
          position: "top-right",
        });
        return;
      }

      // Get pet and service details for display
      const selectedPet = pets.find((pet) => pet._id === petService.pet_id);
      const selectedService = services.find(
        (service) => service._id === petService.service_id,
      );
      const selectedStaff = staffs.find(
        (staff) => staff._id === petService.appointment_to,
      );

      // Extract selected addons
      // const selectedAddons = Object.entries(servicesd || {})
      //   .filter(([_, addon]) => addon.selected)
      //   .map(([addonId, addon]) => ({
      //     addon_id: addonId,
      //     addon_price: Number(addon.price),
      //     name: addon.label,
      //   }));

      // Use clientId from itemData if available (edit mode), otherwise use passed clientId
      const effectiveClientId = itemData?.vendor_client_id || clientId;

      // Helper function to capitalize frequency and numberOfTimes values for API
      const capitalizeFrequency = (freq) => {
        switch (freq) {
          case "one_time":
            return "One Time";
          case "weekly":
            return "Weekly";
          case "monthly":
            return "Monthly";
          case "quaterly":
            return "Quarterly";
          default:
            return freq.charAt(0).toUpperCase() + freq.slice(1);
        }
      };

      const capitalizeNumberOfTimes = (times) => {
        switch (times) {
          case "once":
            return "Once";
          case "twice":
            return "Twice";
          default:
            return times.charAt(0).toUpperCase() + times.slice(1);
        }
      };

      // Create appointment data object (used for both create and update)
      const appointmentData = {
        pet_id: petService.pet_id,
        vendor_pet_id: petService.pet_id, // ensure key for API
        pet_name: selectedPet?.name || "",
        pet_type: petDetails?.pet_type?.name || "",
        pet_size: petDetails?.size?.size_label || "",
        pet_breed: petDetails?.breed?.breed_name || "",
        service_id: petService.service_id,
        service_name: selectedService?.name || "",
        service_category_type_id: serviceTypeId,
        service_type_name:
          selectedService?.service_category?.service_type_name || "Dog walking",
        technician_id: petService.appointment_to,
        employee_name: selectedStaff?.name || "",
        start_date: petService.appointment_date,
        appointment_date: petService.appointment_date,
        start_time: petService.appointment_start_time,
        duration: petService.duration,
        unit_price: petService.price,
        discount: petService.discount,
        gst: petService.gst_amount || 0,
        total_price: calculateTotal().total,
        // addons: selectedAddons,
        addons: itemData?.addons || [],
        item_type: "dog_walking",
        frequency: capitalizeFrequency(petService.frequency),
        numberOfTimes: capitalizeNumberOfTimes(petService.numberOfTimes),
        notes: petService.notes,
        // Ensure ref_id is attached for updates: prefer existing ref_id, fallback to _id / id
        ...(!itemData?._fromEnquiry &&
          itemData?.ref_id && { ref_id: itemData.ref_id }),
        ...(!itemData?._fromEnquiry && !itemData?.ref_id && itemData?._id
          ? { ref_id: itemData._id }
          : {}),
        ...(!itemData?._fromEnquiry &&
        !itemData?.ref_id &&
        !itemData?._id &&
        itemData?.id
          ? { ref_id: itemData.id }
          : {}),
      };

      // Check if this is being called from calendar (AddAppointment)
      if (fromCalendar) {
        // Check if this is an edit operation (has _editIndex)
        if (itemData?._editIndex !== undefined) {
          // This is an update to an existing appointment in the list
          const editIndex = itemData._editIndex;
          const updatedData = {
            ...appointmentData,
            _editIndex: editIndex,
            _originalAppointment: itemData._originalAppointment,
          };
          onUpdate(updatedData);
        } else {
          // This is a new appointment being added
          onUpdate(appointmentData);
        }
        resetFormData();
        onClose();
        return;
      }

      // Prepare payload for API (required for both create & update) - only for non-calendar calls
      const apiPayload = {
        order_type: "offline",
        vendor_client_id: effectiveClientId,
        order_items: [
          {
            item_type: "dog_walking",
            vendor_pet_id: petService.pet_id,
            service_id: petService.service_id,
            technician_id: petService.appointment_to,
            start_date: petService.appointment_date,
            start_time: petService.appointment_start_time,
            duration: petService.duration,
            frequency: capitalizeFrequency(petService.frequency),
            number_of_times: capitalizeNumberOfTimes(petService.numberOfTimes),
            quantity: petService.quantity || 1,
            unit_price: petService.price,
            discount: petService.discount,
            gst: petService.gst_amount || 0,
            notes: petService.notes || "",
            total_price: calculateTotal().total,
            addons: itemData?.addons || [],
            // addons: selectedAddons,
            // include status if available in ref
            ...(appointmentStatusRef?.current && {
              status: appointmentStatusRef.current,
            }),
            // Include ref_id for updates: prefer existing ref_id, fallback to _id / id
            ...(!itemData?._fromEnquiry &&
              itemData?.ref_id && { ref_id: itemData.ref_id }),
            ...(!itemData?._fromEnquiry && !itemData?.ref_id && itemData?._id
              ? { ref_id: itemData._id }
              : {}),
            ...(!itemData?._fromEnquiry &&
            !itemData?.ref_id &&
            !itemData?._id &&
            itemData?.id
              ? { ref_id: itemData.id }
              : {}),
          },
        ],
        ...(orderId && { order_id: orderId }),
        ...(itemData?.order_id && { order_id: itemData.order_id }),
      };

      try {
        const response = await httpService.post(
          "/vendororder/addVendorOrderOrUpdate",
          apiPayload,
        );

        if (response) {
          const action = orderId || itemData?._id ? "updated" : "created";
          // toast.success(`Dog walking appointment ${action} successfully!`);
          onUpdate(response.data);
          resetFormData();
          onClose();
        }
      } catch (error) {
        console.error("Error saving order:", error);
        toast.error("Failed to save appointment");
      }
    };

    // Function to handle time/date changes with confirmation
    const handleTimeDateChange = (newDate, newTime) => {
      if (editMode && itemData) {
        // Store original time data for comparison
        const original = {
          date: petService.appointment_date,
          time: petService.appointment_start_time,
        };

        const newData = {
          date: newDate,
          time: newTime,
        };

        // Check if there's actually a change
        if (original.date !== newData.date || original.time !== newData.time) {
          setOriginalTimeData(original);
          setPendingTimeChange(newData);
          setShowTimeChangeDialog(true);
        } else {
          // No change, apply directly
          setPetService((prev) => ({
            ...prev,
            appointment_date: newDate,
            appointment_start_time: newTime,
          }));
        }
      } else {
        // Not in edit mode, apply directly
        setPetService((prev) => ({
          ...prev,
          appointment_date: newDate,
          appointment_start_time: newTime,
        }));
      }
    };

    // Function to confirm time change
    const confirmTimeChange = () => {
      if (pendingTimeChange) {
        setPetService((prev) => ({
          ...prev,
          appointment_date: pendingTimeChange.date,
          appointment_start_time: pendingTimeChange.time,
        }));
      }
      setShowTimeChangeDialog(false);
      setPendingTimeChange(null);
      setOriginalTimeData(null);
    };

    // Function to cancel time change
    const cancelTimeChange = () => {
      setShowTimeChangeDialog(false);
      setPendingTimeChange(null);
      setOriginalTimeData(null);
    };

    // Function to refresh appointment data
    const refreshAppointmentData = async () => {
      if (itemData?.ref_id && onRefreshData) {
        try {
          const response = await httpService.get(
            `/vendorappointment/getVendorAppointment/${itemData.ref_id}`,
          );
          if (response?.data) {
            onRefreshData(response.data);
            // Update local state with new data
            const newData = response.data;
            setPetService((prev) => ({
              ...prev,
              pet_id: newData.vendor_pet_id || prev.pet_id,
              service_id: newData.service_id || prev.service_id,
              appointment_date: newData.start_date
                ? new Date(newData.start_date).toISOString().split("T")[0]
                : prev.appointment_date,
              appointment_start_time: newData.start_time
                ? convertTo12Hour(newData.start_time)
                : prev.appointment_start_time,
              duration: newData.duration || prev.duration,
              price: newData.price || prev.price,
              discount: newData.discount || prev.discount,
              gst: newData.gst || prev.gst,
              gst: newData.gst || prev.gst,
              totalAmount: newData.total_price || prev.total_price,
              appointment_to: newData.employee_id || prev.appointment_to,
              quantity: newData.quantity || prev.quantity,
            }));
          }
        } catch (error) {
          console.error("Error refreshing appointment data:", error);
        }
      }
    };

    // Expose methods to parent component through ref
    useImperativeHandle(ref, () => ({
      closePetForm: () => {
        setShowNewPetForm(false);
      },
      resetFormData: () => {
        resetFormData();
      },
    }));

    // Add filtered breeds logic like NewServiceDrawer
    // const filteredBreeds = React.useMemo(() => {
    //   if (!newPet.type) return petBreeds;
    //   return petBreeds.filter((b) => b.pet_type_id === newPet.type);
    // }, [petBreeds, newPet.type]);

    // Main component content (stable JSX, not a nested component)
    const mainContent = (
      <>
        {/* Time Change Confirmation Dialog */}
        {showTimeChangeDialog && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">
                Confirm Time Change
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  You're about to change the appointment time:
                </p>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Current:</span>
                    <span className="text-sm">
                      {originalTimeData?.date} at {originalTimeData?.time}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">New:</span>
                    <span className="text-sm text-blue-600">
                      {pendingTimeChange?.date} at {pendingTimeChange?.time}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelTimeChange}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Keep Original
                </button>
                <button
                  onClick={confirmTimeChange}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Use New Time
                </button>
              </div>
            </div>
          </div>
        )}

        {showNewPetForm ? (
          <AddNewPet
            isOpen={showNewPetForm}
            onClose={() => setShowNewPetForm(false)}
            onSave={(addedPetData) => {
              setNewPet({
                type: addedPetData.pet_type,
                name: addedPetData.name,
                gender: addedPetData.gender,
                size: addedPetData.size,
                breed: addedPetData.breed,
                age: addedPetData.age,
                birth_date: addedPetData.birthday,
                behavior_notes: addedPetData.behavior_notes,
                tags: addedPetData.tags,
              });

              setPetService((prev) => ({
                ...prev,
                pet_id: addedPetData._id,
              }));
              fetchPetDetails(addedPetData._id);
              setPets((prevPets) => [...prevPets, addedPetData]);
            }}
            clientId={clientId}
            isFullWidth={true}
            shouldHideSaveAndAdd={true}
            petId={isEditPet ? petDetails._id : null}
          />
        ) : (
          <>
            <Box
              sx={{
                height: renderAsContent ? "auto" : "100vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Remove duplicate header when not in renderAsContent mode */}
              {!renderAsContent && (
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    backgroundColor: "#fff",
                    px: 2,
                    py: 1.5,
                    borderBottom: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  >
                    <FiArrowLeft className="w-5 h-5 text-gray-700" />
                  </button>
                  {/* Heading removed to avoid duplication */}
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  px: 2,
                  py: 1,
                  pb: 10, // Add padding bottom to account for fixed footer
                }}
              >
                {/* Pet Selection (needs to be shown first for service selection) */}
                {!itemData?.ref_id && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <PetsIcon sx={{ fontSize: 16, color: "#3b82f6" }} />
                      Pet <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Box display="flex" gap={1} sx={{ mb: 2 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={petService.pet_id}
                        onChange={(e) => {
                          if (isPetDisabled) return; // Prevent changes when disabled
                          const petId = e.target.value;
                          setPetService((prev) => ({ ...prev, pet_id: petId }));
                          fetchPetDetails(petId);
                        }}
                        disabled={isPetDisabled}
                        placeholder={
                          isPetDisabled ? "Pet pre-selected" : "Select pet"
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PetsIcon
                                sx={{
                                  color: isPetDisabled ? "#9ca3af" : "#6b7280",
                                  fontSize: 18,
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor: isPetDisabled ? "#f9fafb" : "white",
                            border: "1px solid #e5e7eb",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: isPetDisabled ? "#e5e7eb" : "#3b82f6",
                              boxShadow: isPetDisabled
                                ? "none"
                                : "0 4px 12px rgba(59, 130, 246, 0.15)",
                            },
                            "&.Mui-focused": {
                              borderColor: isPetDisabled ? "#e5e7eb" : "#3b82f6",
                              boxShadow: isPetDisabled
                                ? "none"
                                : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                            },
                          },
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#374151",
                          },
                        }}
                      >
                        {isPetDisabled && petService.pet_id ? (
                          // Show only the pre-selected pet when disabled
                          <MenuItem value={petService.pet_id}>
                            {(() => {
                              const selectedPet = pets.find(
                                (pet) => pet._id === petService.pet_id,
                              );
                              if (selectedPet) {
                                const name = selectedPet?.name ?? null;
                                const breedName =
                                  selectedPet?.breed?.breed_name ?? null;
                                const age =
                                  selectedPet?.age &&
                                  selectedPet.age.toString().trim() !== ""
                                    ? `${selectedPet.age} yrs`
                                    : null;
                                const sizeLabel =
                                  selectedPet?.size_details?.size_label ??
                                  selectedPet?.size?.size_label ??
                                  null;

                                const leftChunk = [name, breedName]
                                  .filter(Boolean)
                                  .join(" - ");
                                const pieces = [];
                                if (leftChunk) {
                                  pieces.push(
                                    age ? `${leftChunk} (${age})` : leftChunk,
                                  );
                                } else if (age) {
                                  pieces.push(age);
                                }
                                if (sizeLabel)
                                  pieces.push(sizeLabel.charAt(0).toUpperCase());
                                return pieces.join(" | ");
                              }
                              return "Selected Pet";
                            })()}
                          </MenuItem>
                        ) : (
                          // Show all pets when not pre-selected
                          pets
                          .filter((pet) => pet.status?.toLowerCase() !== "inactive")
                          ?.map((pet) => (
                            <MenuItem key={pet._id} value={pet._id}>
                              {(() => {
                                const name = pet?.name ?? null;
                                const breedName = pet?.breed?.breed_name ?? null;
                                const age =
                                  pet?.age && pet.age.toString().trim() !== ""
                                    ? `${pet.age} yrs`
                                    : null;
                                const sizeLabel =
                                  pet?.size_details?.size_label ??
                                  pet?.size?.size_label ??
                                  null;

                                const leftChunk = [name, breedName]
                                  .filter(Boolean)
                                  .join(" - ");
                                const pieces = [];
                                if (leftChunk) {
                                  pieces.push(
                                    age ? `${leftChunk} (${age})` : leftChunk,
                                  );
                                } else if (age) {
                                  pieces.push(age);
                                }
                                if (sizeLabel)
                                  pieces.push(sizeLabel.charAt(0).toUpperCase());
                                return pieces.join(" | ");
                              })()}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                      {!isPetDisabled && (
                        <>
                          {petDetails?._id && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                setIsEditPet(true);
                                setShowNewPetForm(true);
                              }}
                              sx={{
                                minWidth: "60px",
                                height: "40px",
                                textTransform: "none",
                                borderRadius: "12px",
                                background:
                                  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                border: "none",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "13px",
                                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                  boxShadow:
                                    "0 6px 16px rgba(99, 102, 241, 0.35)",
                                  transform: "translateY(-1px)",
                                  border: "none",
                                },
                                "& .MuiButton-startIcon": {
                                  marginRight: "6px",
                                  "& svg": {
                                    fontSize: "16px",
                                  },
                                },
                              }}
                              style={{
                                display: isPetDisabled ? "none" : "inline-flex",
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {setIsEditPet(true);setShowNewPetForm(true);}}
                            sx={{
                              minWidth: "100px",
                              height: "40px",
                              textTransform: "none",
                              borderRadius: "12px",
                              background:
                                "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                              border: "none",
                              color: "white",
                              fontWeight: 600,
                              fontSize: "13px",
                              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)",
                                boxShadow: "0 6px 16px rgba(99, 102, 241, 0.35)",
                                transform: "translateY(-1px)",
                                border: "none",
                              },
                              "& .MuiButton-startIcon": {
                                marginRight: "6px",
                                "& svg": {
                                  fontSize: "16px",
                                },
                              },
                            }}
                            startIcon={<MdPets />}
                            style={{
                              display: isPetDisabled ? "none" : "inline-flex",
                            }}
                          >
                            Add Pet
                          </Button>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
                {petDetails.length !== 0 && (
                  <Grid item xs={12}>
                    <PetCard pet={petDetails} onEdit={() => {
                      setIsEditPet(true);
                      setShowNewPetForm(true);
                    }}/>
                  </Grid>
                )}
                {/* 1. Date */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <FiCalendar className="w-4 h-4 text-blue-600" />
                      Date
                    </Typography>
                    <div className="relative">
                      <input
                        type="date"
                        lang="en-IN"
                        value={petService.appointment_date}
                        onChange={(e) =>
                          handleTimeDateChange(
                            e.target.value,
                            petService.appointment_start_time,
                          )
                        }
                        min={todayStr}
                        className="w-full pl-12 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white hover:border-blue-400 text-sm"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiCalendar className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </Grid>

                  {/* 2. Time */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <FiClock className="w-4 h-4 text-green-600" />
                      Start Time
                    </Typography>
                    <div className="relative">
                      <select
                        value={petService.appointment_start_time}
                        onChange={(e) =>
                          handleTimeDateChange(
                            petService.appointment_date,
                            e.target.value,
                          )
                        }
                        className="w-full pl-12 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white hover:border-green-400 text-sm"
                      >
                        <option value="">Select time</option>
                        {timeSlots
                          .filter(
                            (slot) =>
                              !isPastTimeSlot(
                                slot,
                                petService.appointment_date,
                              ),
                          )
                          .map((slot) => (
                            <option key={slot} value={slot}>
                              {slot}
                            </option>
                          ))}
                      </select>
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiClock className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </Grid>
                </Grid>

                {/* 3. Frequency and Number of Times */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <RepeatIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                      Frequency <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.frequency}
                      onChange={(e) => {
                        const selectedFrequency = e.target.value;
                        handlePetServiceChange("frequency", selectedFrequency);
                        // Clear numberOfTimes and service selection when frequency changes
                        handlePetServiceChange("numberOfTimes", "");
                        handlePetServiceChange("service_id", "");
                        setPetService((prev) => ({ ...prev, price: 0 }));
                      }}
                      placeholder="Select frequency"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <RepeatIcon
                              sx={{ color: "#6b7280", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "#8b5cf6",
                            boxShadow: "0 4px 12px rgba(139, 92, 246, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#8b5cf6",
                            boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.1)",
                          },
                        },
                      }}
                    >
                      {getFrequencyOptions().map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* 4. Number of Times */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <span style={{ fontSize: 16, color: "#f59e0b" }}>#</span>
                      Number of Times <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={petService.numberOfTimes}
                      onChange={(e) => {
                        const selectedNumberOfTimes = e.target.value;
                        handlePetServiceChange(
                          "numberOfTimes",
                          selectedNumberOfTimes,
                        );
                        // Clear service selection when numberOfTimes changes
                        handlePetServiceChange("service_id", "");
                        setPetService((prev) => ({ ...prev, price: 0 }));
                      }}
                      placeholder="Select number of times"
                      disabled={!petService.frequency}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <span
                              style={{
                                color: "#6b7280",
                                fontSize: 16,
                                fontWeight: "bold",
                              }}
                            >
                              #
                            </span>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "#f59e0b",
                            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
                          },
                          "&.Mui-focused": {
                            borderColor: "#f59e0b",
                            boxShadow: "0 0 0 3px rgba(245, 158, 11, 0.1)",
                          },
                        },
                      }}
                    >
                      {getNumberOfTimesOptions().map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <PetsIcon sx={{ fontSize: 16, color: "#10b981" }} />
                    Service Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={petService.service_id}
                    onChange={async (e) => {
                      const serviceId = e.target.value;

                      setPetService((prev) => ({
                        ...prev,
                        service_id: serviceId,
                      }));

                      // Fetch service details to get duration and other info
                      if (serviceId) {
                        await fetchServiceDetails(serviceId);
                      }
                    }}
                    placeholder="Select dog walking service"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PetsIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#10b981",
                          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#10b981",
                          boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.1)",
                        },
                      },
                    }}
                  >
                    {getAvailableServices()?.map((service) => (
                      <MenuItem
                        key={service._id}
                        value={service._id}
                        sx={{ py: 1 }}
                      >
                        {service?.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 6. Duration */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyDisplay
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: 16, color: "#8b5cf6" }}
                          />
                          Duration
                        </Box>
                      }
                      value={petService.duration || ""}
                      placeholder="Duration will be set automatically"
                      bgColor="#f3f4f6"
                      borderColor="#d1d5db"
                      textColor="#6b7280"
                    />
                  </Grid>

                  {/* 7. Price */}
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyDisplay
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <MdCurrencyRupee
                            style={{ fontSize: 16, color: "#f59e0b" }}
                          />
                          Price
                        </Box>
                      }
                      value={petService.price ? `₹ ${petService.price}` : ""}
                      placeholder="Price will be set automatically"
                      bgColor="#fef3c7"
                      borderColor="#fde68a"
                      textColor="#92400e"
                    />
                  </Grid>
                </Grid>

                {/* GST, Total Amount */}
                <Grid container spacing={1} sx={{ mb: 1, ml: 1 }}>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      mb={0.5}
                      className="flex items-center gap-2"
                    >
                      <ReceiptIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                      Service Charges (%)
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        borderRadius: "12px",
                        backgroundColor: "#f0f9ff",
                        border: "1px solid #bfdbfe",
                        minHeight: "40px",
                        position: "relative",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: petService.tax_percentage
                            ? "#374151"
                            : "#0369a1",
                          fontStyle: petService.tax_percentage
                            ? "normal"
                            : "italic",
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <InputAdornment position="start">
                          <ReceiptIcon
                            sx={{ color: "#6b7280", fontSize: 18 }}
                          />
                        </InputAdornment>
                        {petService.tax_percentage
                          ? petService.tax_percentage
                          : 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <ReadOnlyDisplay
                      label="Total Amount"
                      value={
                        !isNaN(petService.totalAmount) && petService.totalAmount
                          ? `₹ ${petService.totalAmount}`
                          : ""
                      }
                      placeholder="Total will be calculated"
                      bgColor="#f0f9ff"
                      borderColor="#bfdbfe"
                      textColor="#0369a1"
                    />
                  </Grid>
                </Grid>

                {/* Staff Assignment */}
                <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <PersonIcon sx={{ fontSize: 16, color: "#6366f1" }} />
                    Assign Walker
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={petService.appointment_to}
                    onChange={(e) =>
                      handlePetServiceChange("appointment_to", e.target.value)
                    }
                    placeholder="Select walker"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "#6b7280", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#6366f1",
                          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6366f1",
                          boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  >
                    {staffs?.map((staff) => (
                      <MenuItem key={staff._id} value={staff._id}>
                        {staff.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 11. Notes */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    mb={0.5}
                    className="flex items-center gap-2"
                  >
                    <NotesIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                    Notes
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={petService.notes}
                    onChange={(e) =>
                      handlePetServiceChange("notes", e.target.value)
                    }
                    placeholder="Enter any additional notes or special instructions..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NotesIcon
                            sx={{
                              color: "#6b7280",
                              fontSize: 18,
                              alignSelf: "flex-start",
                              mt: 1,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#6b7280",
                          boxShadow: "0 4px 12px rgba(107, 114, 128, 0.15)",
                        },
                        "&.Mui-focused": {
                          borderColor: "#6b7280",
                          boxShadow: "0 0 0 3px rgba(107, 114, 128, 0.1)",
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Payment Fields - Show after Staff Assignment */}
                <div data-payment-fields>{renderPaymentFields()}</div>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: "#fff",
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid #eee",
                  boxShadow: "0 -2px 12px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        resetFormData();
                        onClose();
                      }}
                      sx={{
                        py: 1.4,
                        fontWeight: "bold",
                        fontSize: "16px",
                        borderRadius: "12px",
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {editMode ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Update
                      </Button>
                    ) : fromCalendar ? (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Add
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSaveOrders}
                        sx={{
                          py: 1.4,
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderRadius: "12px",
                          textTransform: "none",
                          boxShadow: 2,
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </>
        )}
      </>
    );

    return renderAsContent ? (
      mainContent
    ) : (
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => {
          resetFormData();
          onClose();
        }}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "80vw",
            maxWidth: 500,
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        {mainContent}
      </Drawer>
    );
  },
);

// Add display name for debugging
DogWalkingDrawer.displayName = "DogWalkingDrawer";

export default DogWalkingDrawer;
